import { useEffect, useState, type ReactNode } from 'react';
import { api, download, fileToBase64 } from '../../lib/api';
import { hasRole } from '../../lib/auth';
import { addDays, today } from '../../lib/dates';
import { T, readableInk, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { humanize, useI18n } from '../../lib/i18n';
import { navigate, setQuery, useLocation } from '../../lib/router';
import {
  AsyncButton, Badge, Button, Card, Checkbox, CopyField, DataTable, Empty, ErrorBox, Field, Input, Loading, PageHeader, Select, Tabs, Textarea, cx, useToast,
} from '../../ui/components';
import { ResourcePage } from '../../ui/schema';
import { CampaignComposer } from './messages';
import { InbodySettings } from './measurements';
import { useCountryOptions } from '../public/auth';

/* ---------- Finance ---------- */
export function FinancePage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'payments';
  const canRefund = hasRole(gym.membership, 'owner', 'finance');
  const [period, setPeriod] = useState(new Date(Date.now() - 20 * 86_400_000).toISOString().slice(0, 7));
  const [exportFrom, setExportFrom] = useState(new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10));
  const [exportTo, setExportTo] = useState(new Date().toISOString().slice(0, 10));
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.finance')} actions={hasRole(gym.membership, 'owner', 'finance') && (
        <AsyncButton variant="secondary" onClick={async () => { const r = await api(`${T(gym.slug)}/admin/dunning/run`, { body: {} }); toast.ok(i18n.t('finance.dunning_done', { count: (r.actions ?? []).length })); }}>{i18n.t('finance.run_dunning')}</AsyncButton>
      )} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'payments', label: i18n.entity('payments') },
        { id: 'invoices', label: i18n.entity('invoices') },
        { id: 'statements', label: i18n.t('team.statements') },
        { id: 'exports', label: i18n.t('finance.exports') },
      ]} />
      {tab === 'payments' && (
        <ResourcePage slug={gym.slug} resource="payments" columns={['person_id', 'kind', 'amount_cents', 'status', 'method', 'paid_at']}
          rowActions={(r, reload) => (canRefund && ['paid', 'partially_refunded'].includes(r.status) ? (
            <AsyncButton size="sm" variant="ghost" onClick={async () => {
              const input = window.prompt(i18n.t('crm.refund_prompt'), String((r.amount_cents - (r.refunded_cents ?? 0)) / 100));
              if (input === null) return;
              await api(`${T(gym.slug)}/admin/payments/${r.id}/refund`, { body: { amountCents: Math.round(Number(input.replace(',', '.')) * 100) } });
              reload();
            }}>{i18n.t('crm.refund')}</AsyncButton>
          ) : null)} />
      )}
      {tab === 'invoices' && <ResourcePage slug={gym.slug} resource="invoices" columns={['number', 'person_id', 'amount_due_cents', 'status', 'due_date', 'dunning_stage']} />}
      {tab === 'statements' && (
        <div className="stack">
          <Card title={i18n.t('team.generate_statements')}>
            <p className="small muted">{i18n.t('team.statements_help')}</p>
            <div className="row gap-sm">
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
              <AsyncButton onClick={async () => { const r = await api(`${T(gym.slug)}/admin/trainer-statements/generate`, { body: { period } }); toast.ok(i18n.t('team.statements_generated', { count: r.length ?? r.count ?? 0 })); }}>{i18n.t('team.generate')}</AsyncButton>
            </div>
          </Card>
          <ResourcePage slug={gym.slug} resource="trainer_statements" searchable={false} />
        </div>
      )}
      {tab === 'exports' && (
        <Card title={i18n.t('finance.exports')}>
          <p className="small muted">{i18n.t('finance.exports_help')}</p>
          <div className="grid-2">
            <Field label={i18n.t('report.month')}><Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} /></Field>
            <Field label={i18n.field('end_date')}><Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} /></Field>
          </div>
          <div className="row gap-sm wrap">
            {(['journal', 'invoices', 'payments', 'vat'] as const).map((kind) => (
              <AsyncButton key={kind} variant="secondary"
                onClick={() => download(`${T(gym.slug)}/admin/exports/bookkeeping.csv?kind=${kind}&from=${exportFrom}&to=${exportTo}`, `${kind}.csv`)}>
                {i18n.t(`finance.export_${kind}`)}
              </AsyncButton>
            ))}
            <AsyncButton variant="secondary" onClick={async () => {
              const rows = await api<any[]>(`${T(gym.slug)}/admin/reports/hours`, { query: { from: exportFrom, to: exportTo } });
              const csv = ['name;shifts;shift_hours;lessons;lesson_hours;total_hours;fee',
                ...rows.map((r) => [r.name, r.shifts, (r.shiftMinutes / 60).toFixed(2), r.lessons, (r.lessonMinutes / 60).toFixed(2), r.totalHours, (r.feeCents / 100).toFixed(2)].join(';'))].join('\r\n');
              const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `hours_${exportFrom}_${exportTo}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}>{i18n.t('finance.export_hours')}</AsyncButton>
            <AsyncButton variant="ghost" onClick={() => download(`${T(gym.slug)}/admin/ads/google-conversions.csv`, 'google-offline-conversions.csv')}>{i18n.t('finance.google_csv')}</AsyncButton>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------- Reports ---------- */
function ObjectTable({ rows }: { rows: Record<string, any>[] }) {
  const i18n = useI18n();
  if (!rows?.length) return <Empty>{i18n.t('common.nothing_here')}</Empty>;
  const keys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== 'object' || rows[0][k] === null);
  const fmt = (k: string, v: any): ReactNode => {
    if (v === null || v === undefined) return '—';
    if (/Cents$|_cents$/.test(k)) return i18n.money(Number(v));
    if (/Rate$/.test(k)) return `${v}%`;
    if (typeof v === 'number') return i18n.num(v);
    if (k === 'month') return i18n.day(`${v}-01`, { month: 'short', year: 'numeric' });
    return i18n.has(`option.${v}`) ? i18n.option(v) : String(v);
  };
  return (
    <DataTable rows={rows.map((r, i) => ({ id: String(i), ...r }))} columns={keys.map((k) => ({
      key: k, label: i18n.has(`report.${k}`) ? i18n.t(`report.${k}`) : humanize(k.replace(/([A-Z])/g, '_$1').toLowerCase()), render: (r) => fmt(k, r[k]),
    }))} />
  );
}

export function ReportsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tz = gym.gym.timezone;
  const tab = query.get('tab') ?? 'funnel';
  const money = hasRole(gym.membership, 'owner', 'manager', 'finance');
  const [from, setFrom] = useState(addDays(today(tz), -30));
  const [to, setTo] = useState(today(tz));
  const range = { from: `${from}T00:00:00.000Z`, to: `${addDays(to, 1)}T00:00:00.000Z` };
  const data = useApi(() => api(`${T(gym.slug)}/admin/reports/${tab}`, { query: tab === 'tokens' ? {} : range }), [tab, from, to]);
  const d: any = data.data;
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.reports')} actions={tab !== 'tokens' && (
        <div className="row gap-sm">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span>–</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      )} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'funnel', label: i18n.t('report.funnel'), hidden: !money },
        { id: 'occupancy', label: i18n.t('report.occupancy') },
        { id: 'revenue', label: i18n.t('report.revenue'), hidden: !money },
        { id: 'tokens', label: i18n.t('report.tokens'), hidden: !money },
        { id: 'classes', label: i18n.t('report.classes'), hidden: !money },
        { id: 'cohorts', label: i18n.t('report.cohorts'), hidden: !money },
        { id: 'forecast', label: i18n.t('report.forecast'), hidden: !money },
        { id: 'targets', label: i18n.t('report.targets'), hidden: !money },
        { id: 'locations', label: i18n.t('report.locations'), hidden: !money },
        { id: 'lead-response', label: i18n.t('report.lead_response') },
        { id: 'trainers', label: i18n.t('report.trainers'), hidden: !money },
      ]} />
      {data.error ? <ErrorBox error={data.error} /> : !d ? <Loading /> : (
        <Card>
          {Array.isArray(d) ? <ObjectTable rows={d} /> : (
            <div className="stack">
              {d.total && <div className="stats">{Object.entries(d.total).filter(([, v]) => typeof v === 'number').map(([k, v]) => <div key={k} className="stat"><div className="stat-label">{humanize(k.replace(/([A-Z])/g, '_$1').toLowerCase())}</div><div className="stat-value">{i18n.num(v as number)}</div></div>)}</div>}
              {'recurringMonthlyCents' in d && (
                <div className="stats">
                  <div className="stat"><div className="stat-label">{i18n.t('report.mrr')}</div><div className="stat-value">{i18n.money(d.recurringMonthlyCents, d.currency)}</div></div>
                  <div className="stat"><div className="stat-label">{i18n.t('report.outstanding')}</div><div className="stat-value">{i18n.money(d.outstandingCents, d.currency)}</div><div className="stat-hint">{d.openInvoices}</div></div>
                </div>
              )}
              {d.totals && (
                <div className="stats">
                  {Object.entries(d.totals).map(([k, v]) => (
                    <div key={k} className="stat">
                      <div className="stat-label">{i18n.t(`report.${k}`)}</div>
                      <div className="stat-value">{k.endsWith('Cents') ? i18n.money(Number(v)) : String(v)}</div>
                    </div>
                  ))}
                </div>
              )}
              {d.cohorts && <ObjectTable rows={d.cohorts} />}
              {d.assumptions && (
                <>
                  <ObjectTable rows={d.months} />
                  <p className="small muted">
                    {i18n.t('report.assumptions', {
                      churn: d.assumptions.monthlyChurnPercent, joining: d.assumptions.newMembersPerMonth,
                      price: i18n.money(d.assumptions.averagePriceCents),
                    })}
                  </p>
                </>
              )}
              {d.metrics && (
                <div className="stack-sm">
                  {d.metrics.map((m: any) => (
                    <div key={m.metric} className="row between wrap">
                      <div>
                        <strong>{i18n.t(`report.metric_${m.metric}`)}</strong>
                        <div className="small muted">
                          {m.metric === 'revenue' ? i18n.money(m.actual) : m.actual}
                          {m.target !== null ? ` / ${m.metric === 'revenue' ? i18n.money(m.target) : m.target}` : ` · ${i18n.t('report.no_target')}`}
                        </div>
                      </div>
                      <div className="row gap-sm">
                        {m.progress !== null && <Badge value={m.progress >= 100 ? 'good' : m.progress >= 70 ? 'warn' : 'bad'} label={`${m.progress}%`} />}
                        <Input type="number" className="input input-sm" defaultValue={m.target ?? ''} style={{ maxWidth: 120 }}
                          onBlur={async (e) => {
                            if (!e.target.value) return;
                            await api(`${T(gym.slug)}/admin/reports/targets`, { method: 'PUT', body: { month: d.month, metric: m.metric, value: Number(e.target.value) } });
                            data.reload();
                          }} />
                      </div>
                    </div>
                  ))}
                  <p className="small muted">{i18n.t('report.target_hint')}</p>
                </div>
              )}
              {d.waiting && (
                <>
                  <div className="stats">
                    <div className="stat"><div className="stat-label">{i18n.t('report.median')}</div><div className="stat-value">{d.medianMinutes === null ? '—' : i18n.t('report.minutes', { count: d.medianMinutes })}</div></div>
                    <div className="stat"><div className="stat-label">{i18n.t('report.within_hour')}</div><div className="stat-value">{d.withinHour === null ? '—' : `${d.withinHour}%`}</div></div>
                    <div className="stat"><div className="stat-label">{i18n.t('report.still_waiting')}</div><div className="stat-value">{d.waiting.length}</div></div>
                  </div>
                  {d.waiting.length > 0 && <ObjectTable rows={d.waiting} />}
                </>
              )}
              {d.sources && <ObjectTable rows={d.sources} />}
              {d.months && d.months[0]?.byKind && <ObjectTable rows={d.months.map((m: any) => ({ month: m.month, totalCents: m.total, ...Object.fromEntries(Object.entries(m.byKind).map(([k, v]) => [`${k}_cents`, v])) }))} />}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ---------- Automations ---------- */
export function AutomationsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'automations';
  const tabs = [
    { id: 'campaigns', resource: 'campaigns', columns: ['name', 'channel', 'status', 'recipients', 'sent_at'] },
    { id: 'automations', resource: 'automations', columns: ['name', 'trigger_event', 'action', 'delay_minutes', 'is_active'] },
    { id: 'knowledge', resource: 'kb_articles', columns: ['title', 'category', 'locale', 'is_active'] },
    { id: 'notifications', resource: 'notifications', columns: ['channel', 'template', 'recipient', 'status', 'created_at'] },
    { id: 'conversions', resource: 'ad_conversions', columns: ['platform', 'event_name', 'status', 'occurred_at'] },
    { id: 'feedback', resource: 'feedback', columns: ['person_id', 'score', 'comment', 'answered_at'] },
    { id: 'referrals', resource: 'referrals', columns: ['referrer_person_id', 'referred_person_id', 'status', 'qualified_at'] },
    { id: 'requests', resource: 'data_requests', columns: ['person_id', 'request_type', 'status', 'created_at'] },
  ];
  const current = tabs.find((t) => t.id === tab) ?? tabs[0];
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.automations')} subtitle={i18n.t(`automations.help_${current.id}`)} />
      <Tabs value={current.id} onChange={(v) => setQuery({ tab: v })} tabs={[...tabs.map((t) => ({ id: t.id, label: i18n.t(`automations.tab_${t.id}`) })), { id: 'new', label: i18n.t('messages.new') }]} />
      {tab === 'new'
        ? <CampaignComposer onSent={() => setQuery({ tab: 'campaigns' })} />
        : <ResourcePage key={current.id} slug={gym.slug} resource={current.resource} columns={current.columns} />}
    </div>
  );
}

/* ---------- Settings ---------- */
const getIn = (obj: any, path: string): any => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
function setIn(obj: any, path: string, value: any): any {
  const [head, ...rest] = path.split('.');
  return { ...obj, [head]: rest.length ? setIn(obj?.[head] ?? {}, rest.join('.'), value) : value };
}

type SettingDef = { path: string; type: 'number' | 'bool' | 'text' | 'select' | 'money' | 'color'; options?: (string | number)[] };
const BOOKING: SettingDef[] = [
  { path: 'booking.cancelCutoffHours', type: 'number' }, { path: 'booking.windowDays', type: 'number' }, { path: 'booking.lateCancelRefund', type: 'bool' },
  { path: 'booking.maxOpenBookingsUnlimited', type: 'number' }, { path: 'booking.graceMinutesAfterPromotion', type: 'number' }, { path: 'booking.blockWhenSuspended', type: 'bool' },
  { path: 'waitlist.enabled', type: 'bool' }, { path: 'waitlist.mode', type: 'select', options: ['auto', 'notify_all'] }, { path: 'waitlist.promotionCutoffHours', type: 'number' },
  { path: 'noShow.alertThreshold', type: 'number' }, { path: 'noShow.windowDays', type: 'number' }, { path: 'noShow.notifyStaff', type: 'bool' },
  { path: 'attendance.unmarkedWhenTaken', type: 'select', options: ['no_show', 'attended'] }, { path: 'attendance.unmarkedWhenNotTaken', type: 'select', options: ['attended', 'no_show'] },
  { path: 'attendance.processAfterMinutes', type: 'number' },
  { path: 'planning.claimMode', type: 'select', options: ['approval', 'instant'] }, { path: 'planning.sessionHorizonWeeks', type: 'number' },
  { path: 'planning.minAttendeesCheckHours', type: 'number' }, { path: 'planning.reminderHours', type: 'number' },
];
const MEMBERS: SettingDef[] = [
  { path: 'tokens.defaultValidity', type: 'select', options: ['end_of_week', 'end_of_month', 'days', 'never'] }, { path: 'tokens.defaultValidityDays', type: 'number' },
  { path: 'tokens.weekStartsOn', type: 'select', options: [1, 0, 6] }, { path: 'tokens.refundValidityDays', type: 'number' },
  { path: 'trial.enabled', type: 'bool' }, { path: 'trial.maxNoShows', type: 'number' }, { path: 'trial.defaultSpotsPerSession', type: 'number' },
  { path: 'dunning.reminder1Days', type: 'number' }, { path: 'dunning.reminder2Days', type: 'number' }, { path: 'dunning.suspendDays', type: 'number' },
  { path: 'dunning.formalNoticeDays', type: 'number' }, { path: 'dunning.escalateDays', type: 'number' },
  { path: 'inactivityDays', type: 'number' }, { path: 'pricesIncludeTax', type: 'bool' }, { path: 'tax.serviceRateBp', type: 'number' }, { path: 'tax.productRateBp', type: 'number' },
  { path: 'mfaRequiredForStaff', type: 'bool' }, { path: 'privacy.anonymizeFormerMembersAfterMonths', type: 'number' }, { path: 'privacy.chatRetentionDays', type: 'number' },
  { path: 'chatbot.enabled', type: 'bool' }, { path: 'chatbot.welcome', type: 'text' },
];
const LEGAL: SettingDef[] = [
  { path: 'legal.withdrawalEnabled', type: 'bool' }, { path: 'legal.withdrawalDays', type: 'number' }, { path: 'legal.euWithdrawalButton', type: 'bool' },
  { path: 'legal.formalNotice', type: 'select', options: ['generic', 'nl_14_day_letter'] }, { path: 'legal.maxNoticeMonths', type: 'number' },
  { path: 'legal.freelancerRateReviewCents', type: 'money' },
  { path: 'legal.termsVersion', type: 'text' }, { path: 'legal.termsUrl', type: 'text' }, { path: 'legal.privacyVersion', type: 'text' }, { path: 'legal.privacyUrl', type: 'text' },
];

function SettingField({ def, value, onChange }: { def: SettingDef; value: any; onChange(v: any): void }) {
  const i18n = useI18n();
  const key = `settings.${def.path}`;
  const label = i18n.t(key);
  const hint = i18n.has(`${key}_hint`) ? i18n.t(`${key}_hint`) : undefined;
  if (def.type === 'bool') return <Checkbox checked={Boolean(value)} onChange={onChange} label={label} hint={hint} />;
  return (
    <Field label={label} hint={hint}>
      {def.type === 'select' ? (
        <Select value={String(value ?? '')} onChange={(e) => onChange(typeof def.options![0] === 'number' ? Number(e.target.value) : e.target.value)}
          options={def.options!.map((o) => ({ value: String(o), label: typeof o === 'number' ? i18n.day(`2024-01-0${o === 0 ? 7 : o}`, { weekday: 'long' }) : i18n.option(String(o)) }))} />
      ) : def.type === 'money' ? (
        <Input type="number" step="0.01" value={value === null || value === undefined ? '' : Number(value) / 100} onChange={(e) => onChange(e.target.value === '' ? null : Math.round(Number(e.target.value) * 100))} />
      ) : def.type === 'number' ? (
        <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
      ) : (
        <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </Field>
  );
}

function CustomDomainCard() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const info = useApi(() => api(`${T(gym.slug)}/admin/domain`), []);
  const [domain, setDomain] = useState('');
  useEffect(() => { if (info.data) setDomain(info.data.domain ?? ''); }, [info.data]);
  if (!info.data) return null;
  return (
    <Card title={i18n.t('settings.custom_domain')} actions={info.data.domain ? <Badge value={info.data.status} /> : null}>
      <p className="small muted">{i18n.t('settings.custom_domain_help', { target: info.data.target })}</p>
      <div className="row gap-sm">
        <Input value={domain} placeholder={`planner.${gym.slug}.nl`} onChange={(e) => setDomain(e.target.value)} />
        <AsyncButton onClick={async () => {
          await api(`${T(gym.slug)}/admin/domain`, { method: 'PUT', body: { domain: domain.trim() || null } });
          toast.ok(i18n.t('common.saved'));
          info.reload();
        }}>{i18n.t('common.save')}</AsyncButton>
      </div>
      {info.data.domain && (
        <>
          <p className="small">{i18n.t('settings.dns_record', { domain: info.data.domain, target: info.data.target })}</p>
          <p className={cx('small', info.data.dns?.pointsHere ? 'good' : 'warn')}>
            {i18n.t(info.data.dns?.pointsHere ? 'settings.dns_ok' : 'settings.dns_pending')}
          </p>
        </>
      )}
    </Card>
  );
}

function PlanCard() {
  const gym = useGym();
  const i18n = useI18n();
  const plan = useApi(() => api(`${T(gym.slug)}/admin/plan`), []);
  if (!plan.data) return null;
  const { usage, plan: p, subscription } = plan.data;
  const line = (label: string, used: number, max: number | null): ReactNode => (
    <div><dt>{label}</dt><dd>{used}{max ? ` / ${max}` : ''}</dd></div>
  );
  return (
    <Card title={i18n.t('settings.plan', { name: p.name })} actions={<Badge value={subscription.status} />}>
      <dl className="dl">
        {line(i18n.entity('locations'), usage.locations, p.maxLocations)}
        {line(i18n.t('dashboard.active_members'), usage.members, p.maxMembers)}
        {line(i18n.t('nav.staff'), usage.staff, p.maxStaff)}
      </dl>
      {subscription.trialDaysLeft !== null && subscription.status === 'trialing' && (
        <p className={cx('small', subscription.trialDaysLeft <= 3 ? 'warn' : 'muted')}>{i18n.t('settings.trial_days', { days: Math.max(0, subscription.trialDaysLeft) })}</p>
      )}
      {p.maxMembers && usage.members > p.maxMembers && <p className="small warn">{i18n.t('settings.over_limit')}</p>}
      <Button variant="secondary" onClick={() => navigate(`${gym.base}/admin/billing`)}>{i18n.t('nav.subscription')}</Button>
    </Card>
  );
}

/** Shows the member's view while the owner picks colours, so nothing has to be saved to see it. */
function BrandPreview({ branding, name }: { branding: Record<string, string>; name: string }) {
  const i18n = useI18n();
  const color = /^#[0-9a-f]{6}$/i.test(branding.primaryColor ?? '') ? branding.primaryColor : '#4f46e5';
  const ink = readableInk(color);
  return (
    <div className="brand-preview" style={{ ['--brand' as string]: color, ['--brand-ink' as string]: ink }}>
      <div className="brand-preview-bar">
        {branding.logoUrl ? <img src={branding.logoUrl} alt="" /> : <strong>{name}</strong>}
        <span>{i18n.t('nav.planner')}</span>
      </div>
      <div className="brand-preview-body">
        <div className="brand-preview-card">
          <strong>{i18n.t('starter.lesson_group') || 'Group training'}</strong>
          <div className="small muted">Mon 19:00 · 6 of 8 spots left</div>
          <button type="button" className="btn btn-primary btn-sm">{i18n.t('planner.book')}</button>
        </div>
      </div>
    </div>
  );
}

function EmbedCodes() {
  const gym = useGym();
  const i18n = useI18n();
  const origin = window.location.origin;
  const trial = `<iframe id="gymsuite-trial" src="${origin}${gym.base}/trial?embed=1" style="width:100%;border:0;min-height:640px" loading="lazy" title="${gym.gym.name}"></iframe>
<script>window.addEventListener("message",function(e){if(e.origin==="${origin}"&&e.data&&e.data.type==="gymsuite:height"){document.getElementById("gymsuite-trial").style.height=e.data.height+"px";}});</script>`;
  const schedule = `<iframe src="${origin}${gym.base}/schedule?embed=1" style="width:100%;border:0;min-height:720px" loading="lazy" title="${gym.gym.name}"></iframe>`;
  const chat = `<iframe src="${origin}${gym.base}/chat-widget" style="position:fixed;right:16px;bottom:16px;width:360px;height:520px;border:0;border-radius:16px;box-shadow:0 8px 30px rgba(0,0,0,.2);z-index:9999" title="Chat"></iframe>`;
  return (
    <div className="stack">
      <Card title={i18n.t('settings.links')}>
        <Field label={i18n.t('settings.link_home')}><CopyField value={`${origin}${gym.base}`} /></Field>
        <Field label={i18n.t('settings.link_trial')}><CopyField value={`${origin}${gym.base}/trial`} /></Field>
        <Field label={i18n.t('settings.link_join')}><CopyField value={`${origin}${gym.base}/join`} /></Field>
      </Card>
      <Card title={i18n.t('settings.embed_trial')}><p className="small muted">{i18n.t('settings.embed_help')}</p><Textarea className="input mono" readOnly rows={5} value={trial} onFocus={(e) => e.target.select()} /></Card>
      <Card title={i18n.t('settings.embed_schedule')}><Textarea className="input mono" readOnly rows={3} value={schedule} onFocus={(e) => e.target.select()} /></Card>
      {gym.chatEnabled && <Card title={i18n.t('settings.embed_chat')}><Textarea className="input mono" readOnly rows={3} value={chat} onFocus={(e) => e.target.select()} /></Card>}
      <p className="small muted">{i18n.t('settings.utm_help')}</p>
    </div>
  );
}

export function SettingsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'general';
  const s = useApi(() => api(`${T(gym.slug)}/admin/settings`), []);
  const audit = useApi(tab === 'audit' ? () => api<any[]>(`${T(gym.slug)}/admin/audit`) : null, [tab]);
  const countries = useCountryOptions();
  const [draft, setDraft] = useState<any>(null);
  useEffect(() => { if (s.data) setDraft({ settings: s.data.settings, branding: s.data.branding ?? {}, profile: s.data.profile }); }, [s.data]);
  const isOwner = hasRole(gym.membership, 'owner');
  if (s.error) return <div className="page"><ErrorBox error={s.error} /></div>;
  if (!draft) return <Loading />;
  const set = (path: string, v: any): void => setDraft((d: any) => setIn(d, path, v));
  const save = async (): Promise<void> => {
    const r = await api(`${T(gym.slug)}/admin/settings`, { method: 'PATCH', body: { settings: draft.settings, branding: draft.branding, profile: isOwner ? draft.profile : undefined } });
    toast.ok(i18n.t('common.saved'));
    if (r.slug && r.slug !== gym.slug) navigate(`/g/${r.slug}/admin/settings?tab=${tab}`);
    else { s.reload(); gym.reload(); }
  };
  const group = (defs: SettingDef[]): ReactNode => (
    <div className="grid-2">{defs.map((d) => <SettingField key={d.path} def={d} value={getIn(draft.settings, d.path)} onChange={(v) => set(`settings.${d.path}`, v)} />)}</div>
  );
  const timezones: string[] = (Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') : [draft.profile.timezone];
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.settings')} actions={tab !== 'website' && tab !== 'audit' && <AsyncButton onClick={save}>{i18n.t('common.save')}</AsyncButton>} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={['general', 'booking', 'members', 'legal', 'languages', 'branding', 'website', 'audit'].map((id) => ({ id, label: i18n.t(`settings.tab_${id}`), hidden: id === 'audit' && !isOwner }))} />
      {tab === 'general' && (
        <Card>
          {!isOwner && <p className="small muted">{i18n.t('settings.owner_only')}</p>}
          <div className="grid-2">
            {Object.keys(draft.profile).map((k) => (
              <Field key={k} label={i18n.field(k)} hint={k === 'slug' ? i18n.t('settings.slug_hint') : undefined}>
                {k === 'country' ? <Select disabled={!isOwner} value={draft.profile[k] ?? ''} onChange={(e) => set(`profile.${k}`, e.target.value)} options={countries} />
                  : k === 'timezone' ? <Select disabled={!isOwner} value={draft.profile[k] ?? ''} onChange={(e) => set(`profile.${k}`, e.target.value)} options={timezones.map((z) => ({ value: z, label: z }))} />
                    : k === 'default_locale' ? <Select disabled={!isOwner} value={draft.profile[k] ?? ''} onChange={(e) => set(`profile.${k}`, e.target.value)} options={i18n.locales.map((l) => ({ value: l.code, label: l.nativeName }))} />
                      : <Input disabled={!isOwner} value={draft.profile[k] ?? ''} onChange={(e) => set(`profile.${k}`, e.target.value)} />}
              </Field>
            ))}
          </div>
        </Card>
      )}
      {tab === 'general' && isOwner && (
        <Card title={i18n.t('settings.export_gym')}>
          <p className="small muted">{i18n.t('settings.export_gym_help')}</p>
          <AsyncButton variant="secondary" onClick={() => download(`${T(gym.slug)}/admin/export.json`, `${gym.slug}-export.json`)}>{i18n.t('settings.export_gym_button')}</AsyncButton>
        </Card>
      )}
      {tab === 'booking' && <Card>{group(BOOKING)}</Card>}
      {tab === 'members' && <Card>{group(MEMBERS)}</Card>}
      {tab === 'legal' && <Card><p className="small muted">{i18n.t('settings.legal_help')}</p>{group(LEGAL)}</Card>}
      {tab === 'languages' && (
        <Card>
          <Field label={i18n.t('settings.locales.default')}>
            <Select value={draft.settings.locales?.default ?? 'en'} onChange={(e) => set('settings.locales.default', e.target.value)} options={i18n.locales.map((l) => ({ value: l.code, label: l.nativeName }))} />
          </Field>
          <p className="small muted">{i18n.t('settings.locales_help')}</p>
          <div className="grid-3">
            {i18n.locales.map((l) => {
              const enabled: string[] = draft.settings.locales?.enabled ?? [];
              return <Checkbox key={l.code} checked={enabled.includes(l.code)} onChange={(c) => set('settings.locales.enabled', c ? [...enabled, l.code] : enabled.filter((x) => x !== l.code))}
                label={<>{l.nativeName} {l.review === 'machine' && <small className="muted">({i18n.t('settings.machine_translated')})</small>}</>} />;
            })}
          </div>
        </Card>
      )}
      {tab === 'branding' && (
        <div className="stack">
          <Card title={i18n.t('settings.brand_basics')}>
            <div className="grid-2">
              <Field label={i18n.t('settings.primary_color')}>
                <div className="row gap-sm">
                  <Input type="color" value={draft.branding.primaryColor ?? '#4f46e5'} onChange={(e) => set('branding.primaryColor', e.target.value)} />
                  <Input value={draft.branding.primaryColor ?? '#4f46e5'} onChange={(e) => set('branding.primaryColor', e.target.value)} />
                </div>
              </Field>
              <Field label={i18n.t('settings.font')} hint={i18n.t('settings.font_hint')}>
                <Select value={draft.branding.font ?? 'system'} onChange={(e) => set('branding.font', e.target.value)}
                  options={['system', 'inter', 'rounded', 'serif', 'condensed'].map((f) => ({ value: f, label: i18n.t(`settings.font_${f}`) }))} />
              </Field>
              {([['logoUrl', 'logo'], ['logoDarkUrl', 'logo_dark'], ['iconUrl', 'icon'], ['heroImageUrl', 'hero_image']] as const).map(([key, label]) => (
                <Field key={key} label={i18n.t(`settings.${label}`)} hint={i18n.t(`settings.${label}_hint`)}>
                  <div className="row gap-sm wrap">
                    {draft.branding[key] && <img src={draft.branding[key]} alt="" className="logo-preview" />}
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const r = await api(`${T(gym.slug)}/admin/uploads`, { body: { contentType: f.type, dataBase64: await fileToBase64(f), visibility: 'public' } });
                        set(`branding.${key}`, r.url);
                      } catch (err) { toast.error(err); }
                    }} />
                    {draft.branding[key] && <Button size="sm" variant="ghost" onClick={() => set(`branding.${key}`, '')}>{i18n.t('common.remove')}</Button>}
                  </div>
                </Field>
              ))}
              <Field label={i18n.t('settings.hero_text')} className="span-2"><Textarea rows={2} value={draft.branding.heroText ?? ''} onChange={(e) => set('branding.heroText', e.target.value)} /></Field>
            </div>
          </Card>
          <Card title={i18n.t('settings.preview')}>
            <p className="small muted">{i18n.t('settings.preview_hint')}</p>
            <BrandPreview branding={draft.branding} name={gym.gym.name} />
          </Card>
        </div>
      )}
      {tab === 'website' && <><PlanCard /><CustomDomainCard /><EmbedCodes /></>}
      {tab === 'audit' && (
        <Card>
          {!audit.data ? <Loading /> : <DataTable rows={audit.data} columns={[
            { key: 'created_at', label: i18n.field('created_at'), render: (r) => i18n.dateTime(r.created_at) },
            { key: 'user', label: i18n.t('settings.who'), render: (r) => r.user_email ?? r.user_id ?? '—' },
            { key: 'action', label: i18n.field('action') },
            { key: 'entity', label: i18n.t('settings.what'), render: (r) => `${r.entity ?? ''} ${r.entity_id ? `#${String(r.entity_id).slice(0, 8)}` : ''}` },
          ]} />}
        </Card>
      )}
    </div>
  );
}

/* ---------- Integrations ---------- */
export function IntegrationsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const data = useApi(() => api(`${T(gym.slug)}/admin/integrations`), []);
  const [meta, setMeta] = useState<Record<string, string>>({});
  const [forms, setForms] = useState('');
  const [names, setNames] = useState('');
  const isOwner = hasRole(gym.membership, 'owner');
  useEffect(() => {
    if (query.get('stripe')) api(`${T(gym.slug)}/admin/stripe/refresh`, { body: {} }).then(() => { data.reload(); gym.reload(); }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!data.data) return;
    setForms(JSON.stringify(data.data.meta.formLocations ?? {}, null, 2));
    setNames(JSON.stringify(data.data.google_ads.conversionNames ?? {}, null, 2));
  }, [data.data]);
  if (data.error) return <div className="page"><ErrorBox error={data.error} /></div>;
  if (!data.data) return <Loading />;
  const d = data.data;
  const parse = (text: string): any => { try { return text.trim() ? JSON.parse(text) : {}; } catch { throw new Error(i18n.t('form.invalid_json')); } };
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.integrations')} />
      <Card title={i18n.t('integrations.payments')} actions={<Badge value={d.payments.chargesEnabled ? 'connected' : d.payments.connected ? 'onboarding' : 'pending'} />}>
        <p className="small muted">{i18n.t('integrations.payments_help', { provider: d.payments.provider })}</p>
        <dl className="dl">
          <div><dt>{i18n.t('integrations.account')}</dt><dd>{d.payments.accountId ?? '—'}</dd></div>
          <div><dt>{i18n.t('integrations.charges')}</dt><dd>{d.payments.chargesEnabled ? '✓' : '—'}</dd></div>
          <div><dt>{i18n.t('integrations.details')}</dt><dd>{d.payments.detailsSubmitted ? '✓' : '—'}</dd></div>
        </dl>
        {isOwner && (
          <div className="row gap-sm">
            {!d.payments.chargesEnabled && <AsyncButton onClick={async () => { const r = await api(`${T(gym.slug)}/admin/stripe/connect`, { body: {} }); window.location.href = r.url; }}>{i18n.t(d.payments.connected ? 'integrations.continue_onboarding' : 'integrations.connect')}</AsyncButton>}
            {d.payments.connected && <AsyncButton variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/stripe/refresh`, { body: {} }); data.reload(); gym.reload(); }}>{i18n.t('integrations.refresh')}</AsyncButton>}
          </div>
        )}
      </Card>
      <Card title="Meta (Facebook / Instagram)" actions={<Badge value={d.meta.status} />}>
        <p className="small muted">{i18n.t('integrations.meta_help')}</p>
        <Field label={i18n.t('integrations.webhook_url')}><CopyField value={d.meta.webhookUrl} /></Field>
        <div className="grid-2">
          <Field label={i18n.t('integrations.page_id')}><Input defaultValue={d.meta.pageId ?? ''} onChange={(e) => setMeta({ ...meta, page_id: e.target.value })} /></Field>
          <Field label={i18n.t('integrations.pixel_id')}><Input defaultValue={d.meta.pixelId ?? ''} onChange={(e) => setMeta({ ...meta, pixel_id: e.target.value })} /></Field>
          <Field label={i18n.t('integrations.page_token')}><Input type="password" autoComplete="off" placeholder={d.meta.pageAccessToken ?? ''} onChange={(e) => setMeta({ ...meta, page_access_token: e.target.value })} /></Field>
          <Field label={i18n.t('integrations.capi_token')}><Input type="password" autoComplete="off" placeholder={d.meta.capiAccessToken ?? ''} onChange={(e) => setMeta({ ...meta, capi_access_token: e.target.value })} /></Field>
        </div>
        <Field label={i18n.t('integrations.form_locations')} hint={i18n.t('integrations.form_locations_hint')}><Textarea className="input mono" rows={3} value={forms} onChange={(e) => setForms(e.target.value)} /></Field>
        {isOwner && <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/admin/integrations/meta`, { method: 'PUT', body: { ...meta, form_locations: parse(forms) } }); toast.ok(i18n.t('common.saved')); data.reload(); }}>{i18n.t('common.save')}</AsyncButton>}
      </Card>
      <Card title="Google Ads" actions={<Badge value={d.google_ads.status} />}>
        <p className="small muted">{i18n.t('integrations.google_help')}</p>
        <Field label={i18n.t('integrations.webhook_url')}><CopyField value={d.google_ads.webhookUrl} /></Field>
        {d.google_ads.webhookKey && <Field label={i18n.t('integrations.webhook_key')}><CopyField value={d.google_ads.webhookKey} /></Field>}
        <Field label={i18n.t('integrations.conversion_names')} hint={i18n.t('integrations.conversion_names_hint')}><Textarea className="input mono" rows={3} value={names} onChange={(e) => setNames(e.target.value)} /></Field>
        {isOwner && (
          <div className="row gap-sm wrap">
            <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/admin/integrations/google_ads`, { method: 'PUT', body: { conversion_names: parse(names) } }); toast.ok(i18n.t('common.saved')); data.reload(); }}>{i18n.t('common.save')}</AsyncButton>
            <AsyncButton variant="secondary" confirm={i18n.t('integrations.rotate_confirm')} onClick={async () => { await api(`${T(gym.slug)}/admin/integrations/google_ads`, { method: 'PUT', body: { rotate_key: true } }); data.reload(); }}>{i18n.t('integrations.rotate')}</AsyncButton>
            <AsyncButton variant="ghost" onClick={() => download(`${T(gym.slug)}/admin/ads/google-conversions.csv`, 'google-offline-conversions.csv')}>{i18n.t('finance.google_csv')}</AsyncButton>
          </div>
        )}
      </Card>
      <InbodySettings />

      <Card title={i18n.t('integrations.platform_services')}>
        <p className="small muted">{i18n.t('integrations.platform_help')}</p>
        <dl className="dl">
          <div><dt>{i18n.t('integrations.email')}</dt><dd>{d.email.provider}</dd></div>
          <div><dt>{i18n.t('integrations.sms')}</dt><dd>{d.sms.provider}</dd></div>
          <div><dt>{i18n.t('integrations.chatbot')}</dt><dd>{d.chatbot.provider}{d.chatbot.model ? ` (${d.chatbot.model})` : ''}</dd></div>
          <div><dt>{i18n.t('integrations.pdf')}</dt><dd>{d.pdf.enabled ? '✓' : '—'}</dd></div>
        </dl>
      </Card>
    </div>
  );
}

/* ---------- Platform subscription ---------- */
export function BillingPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const data = useApi(() => api(`${T(gym.slug)}/admin/billing`), []);
  if (!data.data) return <Loading />;
  const d = data.data;
  return (
    <div className="page narrow">
      <PageHeader title={i18n.t('nav.subscription')} />
      {query.get('subscribed') && <div className="alert alert-ok">{i18n.t('billing.thanks')}</div>}
      <Card>
        <dl className="dl">
          <div><dt>{i18n.field('status')}</dt><dd><Badge value={d.platformStatus ?? d.status} /></dd></div>
          {d.trialEndsAt && <div><dt>{i18n.t('billing.trial_ends')}</dt><dd>{i18n.date(d.trialEndsAt)}</dd></div>}
        </dl>
        <p className="small muted">{i18n.t(d.selfService === false ? 'billing.contact_operator' : 'billing.help')}</p>
        <div className="row gap-sm">
          {d.platformStatus !== 'active' && d.selfService !== false && <AsyncButton onClick={async () => { const r = await api(`${T(gym.slug)}/admin/billing/subscribe`, { body: {} }); window.location.href = r.url; }}>{i18n.t('billing.subscribe')}</AsyncButton>}
          {d.hasCustomer && <AsyncButton variant="secondary" onClick={async () => { const r = await api(`${T(gym.slug)}/admin/billing/portal`, { body: {} }); window.location.href = r.url; }}>{i18n.t('billing.portal')}</AsyncButton>}
        </div>
      </Card>
    </div>
  );
}
